import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Box, Typography,
    IconButton, TextField, Button, Chip, CircularProgress,
    Alert, InputAdornment, Autocomplete, Tabs, Tab,
} from '@mui/material';
import {
    Close, Edit, Delete, Check, Search as SearchIcon, DragIndicator,
} from '@mui/icons-material';
import {
    DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { businessAPI } from '../api';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { productCategoryList, mergeCategoryOrder } from '../utils/groupByCategory';

// Stop a pointer-down on interactive controls (buttons, inputs) from starting a drag.
const noDrag = { onPointerDown: (e) => e.stopPropagation() };

// A draggable row. The WHOLE card is the drag target (drag from anywhere); the grip
// icon is just a visual cue. On touch, dragging uses a short press-and-hold so the
// list can still be scrolled with a normal swipe.
const SortableRow = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <Box ref={setNodeRef} style={style} {...attributes} {...listeners}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                bgcolor: isDragging ? 'primary.50' : 'background.paper',
                cursor: 'grab', userSelect: 'none', touchAction: 'manipulation',
                '&:active': { cursor: 'grabbing' },
            }}>
            <DragIndicator sx={{ color: 'text.disabled', flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Box>
    );
};

const ProductCategoryManager = ({ open, onClose, businessId, businessProducts, onProductsUpdated }) => {
    const [tab, setTab] = useState(0);

    // Category order (server-saved names) + product order (local, saved on drag)
    const [savedOrder, setSavedOrder] = useState([]);
    const [productOrder, setProductOrder] = useState([]);

    // Rename
    const [renamingCat, setRenamingCat] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [renameLoading, setRenameLoading] = useState(false);
    const [renameError, setRenameError] = useState('');

    // Delete
    const [deletingCat, setDeletingCat] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Bulk assign
    const [bulkSearch, setBulkSearch] = useState('');
    const [bulkFilter, setBulkFilter] = useState('no_category'); // null=All | 'no_category' | category name
    const [bulkSelected, setBulkSelected] = useState([]);
    const [bulkCategory, setBulkCategory] = useState('');
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    const sensors = useSensors(
        // Mouse: start dragging after a small move. Touch: press-and-hold briefly so a
        // plain swipe still scrolls the list.
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    );

    const distinctCats = useMemo(
        () => [...new Set((businessProducts || []).flatMap(p => productCategoryList(p)))],
        [businessProducts]
    );
    // Categories in display order: saved order first, then any new/unlisted ones.
    const categories = useMemo(() => mergeCategoryOrder(distinctCats, savedOrder), [distinctCats, savedOrder]);

    const productsByCategory = cat => (businessProducts || []).filter(p => productCategoryList(p).includes(cat));

    // On open: load saved category order and seed the product arrangement (by current sort order).
    useEffect(() => {
        if (!open) return;
        if (businessId) {
            businessAPI.getCategoryOrder(businessId)
                .then(res => setSavedOrder(res.data?.ordered_names || []))
                .catch(() => setSavedOrder([]));
        }
        setProductOrder(
            [...(businessProducts || [])]
                .filter(p => !p.f_is_archived)
                .sort((a, b) => (a.f_sort_order ?? 0) - (b.f_sort_order ?? 0) || (a.f_name || '').localeCompare(b.f_name || ''))
        );
    }, [open, businessId, businessProducts]);

    const handleCatDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = categories.indexOf(active.id);
        const newIndex = categories.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const newList = arrayMove(categories, oldIndex, newIndex);
        setSavedOrder(newList);
        if (businessId) businessAPI.saveCategoryOrder(businessId, newList).catch(() => {});
    };

    const handleProdDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = productOrder.findIndex(p => p.f_id === active.id);
        const newIndex = productOrder.findIndex(p => p.f_id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const newArr = arrayMove(productOrder, oldIndex, newIndex);
        setProductOrder(newArr);
        if (businessId) businessAPI.reorderProducts(businessId, newArr.map(p => p.f_id)).catch(() => {});
    };

    const handleRename = async (oldCat) => {
        const newCat = renameValue.trim();
        if (!newCat || newCat === oldCat) { setRenamingCat(null); return; }
        setRenameLoading(true);
        setRenameError('');
        try {
            for (const p of productsByCategory(oldCat)) {
                const next = [...new Set(productCategoryList(p).map(c => c === oldCat ? newCat : c))];
                await businessAPI.updateBusinessProduct(p.f_id, { f_categories: next });
            }
            const newOrder = [...new Set(categories.map(c => c === oldCat ? newCat : c))];
            setSavedOrder(newOrder);
            if (businessId) await businessAPI.saveCategoryOrder(businessId, newOrder);
            setRenamingCat(null);
            onProductsUpdated();
        } catch {
            setRenameError('Failed to rename. Please try again.');
        } finally {
            setRenameLoading(false);
        }
    };

    const handleDelete = async (cat) => {
        setDeleteLoading(true);
        try {
            for (const p of productsByCategory(cat)) {
                const next = productCategoryList(p).filter(c => c !== cat);
                await businessAPI.updateBusinessProduct(p.f_id, { f_categories: next });
            }
            const newOrder = categories.filter(c => c !== cat);
            setSavedOrder(newOrder);
            if (businessId) await businessAPI.saveCategoryOrder(businessId, newOrder);
            setDeletingCat(null);
            onProductsUpdated();
        } catch {
            // silent — partial failures leave remaining products unchanged
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleBulkAssign = async () => {
        if (!bulkSelected.length) return;
        setBulkLoading(true);
        setBulkResult(null);
        const target = bulkCategory.trim();
        let success = 0; let failed = 0;
        for (const id of bulkSelected) {
            try {
                const p = (businessProducts || []).find(x => x.f_id === id);
                let next;
                if (target) {
                    // Add to the category (keep any existing memberships)
                    const existing = productCategoryList(p);
                    next = existing.includes(target) ? existing : [...existing, target];
                } else {
                    next = []; // empty = clear all categories
                }
                await businessAPI.updateBusinessProduct(id, { f_categories: next });
                success++;
            } catch { failed++; }
        }
        setBulkLoading(false);
        setBulkResult({ success, failed });
        setBulkSelected([]);
        onProductsUpdated();
    };

    const filteredProducts = (businessProducts || []).filter(p => {
        if (p.f_is_archived) return false;
        const cats = productCategoryList(p);
        if (bulkFilter === 'no_category' && cats.length) return false;
        if (bulkFilter && bulkFilter !== 'no_category' && !cats.includes(bulkFilter)) return false;
        return !bulkSearch || p.f_name?.toLowerCase().includes(bulkSearch.toLowerCase());
    });
    const allFilteredIds = filteredProducts.map(p => p.f_id);
    const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => bulkSelected.includes(id));

    const handleClose = () => {
        setRenamingCat(null); setDeletingCat(null);
        setBulkSelected([]); setBulkSearch(''); setBulkFilter('no_category'); setBulkCategory(''); setBulkResult(null);
        setTab(0);
        onClose();
    };

    return (
        // Shared full-page dialog: keeps clear of the notch (title), the gesture bar and side notches
        // (paper), and is a standard centred size on desktop. Fixed height so switching tabs doesn't jump.
        <Dialog open={open} onClose={handleClose} disableScrollLock={false} {...APP_DIALOG}
            PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, pb: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Typography variant="h6">Sort & Categories</Typography>
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}><Close /></IconButton>
                </Box>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
                    sx={{
                        '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 500, minWidth: 0, fontSize: '0.8rem', px: 1 },
                        '& .Mui-selected': { color: 'white !important' },
                        '& .MuiTabs-indicator': { bgcolor: 'white' },
                    }}>
                    <Tab label="Categories" />
                    <Tab label="Products" />
                    <Tab label="Bulk Assign" />
                </Tabs>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* ── Arrange Categories tab ── */}
                {tab === 0 && (
                    <Box>
                        {categories.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="body2" color="text.secondary">No categories yet.</Typography>
                                <Typography variant="caption" color="text.disabled">Assign a category to a product from the Info dialog.</Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
                                    Drag to set the order customers see. Tap to rename or remove.
                                </Typography>
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCatDragEnd}>
                                    <SortableContext items={categories} strategy={verticalListSortingStrategy}>
                                        {categories.map(cat => {
                                            const count = productsByCategory(cat).length;
                                            const isRenaming = renamingCat === cat;
                                            const isDeleting = deletingCat === cat;
                                            return (
                                                <SortableRow key={cat} id={cat}>
                                                    {isRenaming ? (
                                                        <>
                                                            <Box {...noDrag} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                <TextField
                                                                    size="small" fullWidth autoFocus
                                                                    value={renameValue}
                                                                    onChange={e => setRenameValue(e.target.value)}
                                                                    onKeyDown={e => {
                                                                        if (e.key === 'Enter') handleRename(cat);
                                                                        if (e.key === 'Escape') setRenamingCat(null);
                                                                    }}
                                                                    disabled={renameLoading}
                                                                />
                                                                <IconButton size="small" color="primary"
                                                                    onClick={() => handleRename(cat)} disabled={renameLoading}>
                                                                    {renameLoading ? <CircularProgress size={16} /> : <Check />}
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => setRenamingCat(null)} disabled={renameLoading}>
                                                                    <Close fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                            {renameError && <Alert severity="error" sx={{ mt: 0.75 }}>{renameError}</Alert>}
                                                        </>
                                                    ) : isDeleting ? (
                                                        <Box {...noDrag} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                                                Remove <strong>{cat}</strong> from {count} product{count !== 1 ? 's' : ''}?
                                                            </Typography>
                                                            <Button size="small" color="error" variant="contained" disableElevation
                                                                onClick={() => handleDelete(cat)} disabled={deleteLoading}>
                                                                {deleteLoading ? <CircularProgress size={14} color="inherit" /> : 'Remove'}
                                                            </Button>
                                                            <Button size="small" onClick={() => setDeletingCat(null)} disabled={deleteLoading}>Cancel</Button>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body2" fontWeight={500} noWrap>{cat}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {count} product{count !== 1 ? 's' : ''}
                                                                </Typography>
                                                            </Box>
                                                            <IconButton size="small" {...noDrag}
                                                                onClick={() => { setRenamingCat(cat); setRenameValue(cat); setRenameError(''); }}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" sx={{ color: 'error.main' }} {...noDrag}
                                                                onClick={() => setDeletingCat(cat)}>
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </SortableRow>
                                            );
                                        })}
                                    </SortableContext>
                                </DndContext>
                            </>
                        )}
                    </Box>
                )}

                {/* ── Arrange Products tab ── */}
                {tab === 1 && (
                    <Box>
                        {productOrder.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="body2" color="text.secondary">No products yet.</Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
                                    Drag to set the order products appear within each category.
                                </Typography>
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleProdDragEnd}>
                                    <SortableContext items={productOrder.map(p => p.f_id)} strategy={verticalListSortingStrategy}>
                                        {productOrder.map(p => {
                                            const cats = productCategoryList(p);
                                            return (
                                                <SortableRow key={p.f_id} id={p.f_id}>
                                                    <Typography variant="body2" fontWeight={500} noWrap>{p.f_name}</Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                                                        {cats.length ? cats.map(c => (
                                                            <Chip key={c} label={c} size="small" variant="outlined"
                                                                sx={{ height: 18, fontSize: '0.65rem' }} />
                                                        )) : (
                                                            <Typography variant="caption" color="text.disabled">No category</Typography>
                                                        )}
                                                    </Box>
                                                </SortableRow>
                                            );
                                        })}
                                    </SortableContext>
                                </DndContext>
                            </>
                        )}
                    </Box>
                )}

                {/* ── Bulk Assign tab ── */}
                {tab === 2 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ px: 2, pt: 1.5, pb: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Autocomplete
                                freeSolo
                                options={categories}
                                value={bulkCategory}
                                inputValue={bulkCategory}
                                onChange={(_, val) => setBulkCategory(val || '')}
                                onInputChange={(_, val) => setBulkCategory(val)}
                                renderInput={params => (
                                    <TextField {...params} size="small" fullWidth
                                        label="Add to category"
                                        placeholder="Type or pick a category"
                                        sx={{ mb: 1 }}
                                    />
                                )}
                            />
                            <TextField
                                size="small" fullWidth placeholder="Search products…"
                                value={bulkSearch}
                                onChange={e => setBulkSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                {[
                                    { value: null, label: 'All' },
                                    { value: 'no_category', label: 'No category' },
                                    ...categories.map(c => ({ value: c, label: c })),
                                ].map(opt => (
                                    <Chip key={opt.value ?? 'all'} label={opt.label} size="small" clickable
                                        color={bulkFilter === opt.value ? 'primary' : 'default'}
                                        variant={bulkFilter === opt.value ? 'filled' : 'outlined'}
                                        onClick={() => { setBulkFilter(opt.value); setBulkSelected([]); }}
                                    />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {bulkSelected.length} selected · {filteredProducts.length} shown
                                </Typography>
                                {filteredProducts.length > 0 && (
                                    <Button size="small" sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0, minWidth: 0 }}
                                        onClick={() => allSelected ? setBulkSelected([]) : setBulkSelected(allFilteredIds)}>
                                        {allSelected ? 'Deselect all' : 'Select all'}
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        {filteredProducts.map(p => {
                            const isSelected = bulkSelected.includes(p.f_id);
                            return (
                                <Box key={p.f_id}
                                    onClick={() => setBulkSelected(prev =>
                                        isSelected ? prev.filter(id => id !== p.f_id) : [...prev, p.f_id]
                                    )}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                        px: 2, py: 1.25,
                                        borderBottom: '1px solid', borderColor: 'divider',
                                        cursor: 'pointer',
                                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                        userSelect: 'none',
                                        '&:active': { bgcolor: 'action.selected' },
                                    }}>
                                    <Chip size="small"
                                        label={isSelected ? '✓' : '+'}
                                        color={isSelected ? 'primary' : 'default'}
                                        variant={isSelected ? 'filled' : 'outlined'}
                                        sx={{ width: 32, height: 24, fontSize: '0.8rem', flexShrink: 0, cursor: 'inherit' }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={500} noWrap>{p.f_name}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {productCategoryList(p).join(' · ') || 'No category'}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}

                        {bulkResult && (
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Alert severity={bulkResult.failed > 0 ? 'warning' : 'success'}>
                                    {bulkResult.success} product{bulkResult.success !== 1 ? 's' : ''} updated
                                    {bulkResult.failed > 0 ? `, ${bulkResult.failed} failed` : ' successfully'}.
                                </Alert>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            {tab === 2 && (
                <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                        variant="contained" fullWidth disableElevation
                        disabled={bulkSelected.length === 0 || bulkLoading}
                        onClick={handleBulkAssign}
                        startIcon={bulkLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ textTransform: 'none' }}
                    >
                        {bulkLoading ? 'Saving…' : (bulkCategory.trim()
                            ? `Add to “${bulkCategory.trim()}” (${bulkSelected.length})`
                            : `Clear category (${bulkSelected.length})`)}
                    </Button>
                </Box>
            )}
        </Dialog>
    );
};

export default ProductCategoryManager;
